import React from 'react';
import { Link } from 'react-router-dom';
import MarketingHeader from './MarketingHeader';
import HomeIcon from './HomeIcon';
import DocsMarkdown from '../../components/docs/DocsMarkdown';
import type { BlogLandingVals } from './useBlogLanding';
import { getBlogCopy } from './blogCopy';
import {
  estimateReadMinutes,
  formatBlogDate,
  type BlogPostDetail,
  type BlogPostListItem,
  type BlogRelated,
} from './blogTypes';
import './HomeLanding.css';

type Props = {
  v: BlogLandingVals;
  lang: 'cs' | 'en';
  onSetCz: () => void;
  onSetEn: () => void;
  mode: 'list' | 'article';
  posts: BlogPostListItem[];
  listLoading: boolean;
  listError: boolean;
  article: BlogPostDetail | null;
  related: BlogRelated[];
  articleLoading: boolean;
  articleMissing: boolean;
};

const socialBtnStyle: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 10,
  background: '#F7F8FA',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#6B7280',
  transition: 'all 150ms ease-out',
};

const footerSocialStyle: React.CSSProperties = {
  ...socialBtnStyle,
  width: 34,
  height: 34,
};

/** Brand social marks (same as About / Contact footers). */
const SocialLinks: React.FC<{ size?: 'hero' | 'footer' }> = ({ size = 'hero' }) => {
  const style = size === 'footer' ? footerSocialStyle : socialBtnStyle;
  const icon = size === 'footer' ? 17 : 18;
  return (
    <>
      <a href="#li" aria-label="LinkedIn" className="home-social" style={style}>
        <svg width={icon} height={icon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM2.75 9.25h4.46V21H2.75V9.25zm7.02 0h4.27v1.6h.06c.6-1.07 2.06-1.94 3.79-1.94 3.24 0 4.36 1.94 4.36 5.35V21h-4.46v-5.72c0-1.44-.52-2.42-1.8-2.42-1.05 0-1.67.71-1.94 1.4-.1.24-.13.58-.13.92V21H9.77s.06-10.63 0-11.75z" />
        </svg>
      </a>
      <a href="#fb" aria-label="Facebook" className="home-social" style={style}>
        <svg width={icon} height={icon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z" />
        </svg>
      </a>
      <a href="#ig" aria-label="Instagram" className="home-social" style={style}>
        <svg width={icon} height={icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.3" cy="6.7" r="1.1" fill="currentColor" stroke="none" />
        </svg>
      </a>
    </>
  );
};

const TOPIC_ICONS = ['calendar', 'document', 'bell'] as const;

const BlogLanding: React.FC<Props> = ({
  v,
  lang,
  onSetCz,
  onSetEn,
  mode,
  posts,
  listLoading,
  listError,
  article,
  related,
  articleLoading,
  articleMissing,
}) => {
  const c = getBlogCopy(lang);
  const heroPost = posts[0];

  const footer = (
    <footer style={{ background: '#fff', borderTop: '1px solid #EDF0F5', padding: 'clamp(44px,7vw,64px) 20px 32px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div
          className="home-blog-footer-grid"
          style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr 1fr 1fr', gap: 40 }}
        >
          <div>
            <img src="/home/ompli-logo-blue.png" alt="Ompli" style={{ height: 24, width: 'auto', display: 'block' }} />
            <p style={{ margin: '16px 0 0', fontSize: 15, lineHeight: 1.6, color: '#6B7280', maxWidth: 260 }}>
              {c.footerTagline}
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <SocialLinks size="footer" />
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerProduct}</div>
            <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerShifts}</a>
            <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerAttendance}</a>
            <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerContracts}</a>
            <a href="/#funkce" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerHiring}</a>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerCompany}</div>
            <a href="/o-nas" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerAbout}</a>
            <a href="#kariera" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerCareers}</a>
            <a href="/blog" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerBlog}</a>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerSupport}</div>
            <a href="/docs" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerHelp}</a>
            <a href="/kontakt" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerContact}</a>
            <a href="#status" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerStatus}</a>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{c.footerLegal}</div>
            <a href="#gdpr" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerGdpr}</a>
            <a href="#podminky" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerTerms}</a>
            <a href="#cookies" className="home-footer-link" style={{ fontSize: 15, color: '#6B7280' }}>{c.footerCookies}</a>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 24,
            flexWrap: 'wrap',
            marginTop: 48,
            paddingTop: 24,
            borderTop: '1px solid #EDF0F5',
          }}
        >
          <span style={{ fontSize: 14, color: '#6B7280' }}>© 2026 Ompli s.r.o.</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 14, color: '#6B7280' }}>{v.langLabel} · CZK</span>
            <a href="#privacy" className="home-footer-link" style={{ fontSize: 14, color: '#6B7280' }}>{c.footerPrivacy}</a>
            <a href="#terms" className="home-footer-link" style={{ fontSize: 14, color: '#6B7280' }}>{c.footerTermsShort}</a>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="home-landing" style={{ width: '100%', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <MarketingHeader v={v} lang={lang} onSetCz={onSetCz} onSetEn={onSetEn} active="blog" />

      {mode === 'list' && (
        <div id="top">
          <section
            style={{
              position: 'relative',
              padding: 'clamp(112px,18vw,156px) 20px clamp(48px,8vw,80px)',
              background:
                'radial-gradient(1100px 620px at 50% -10%, #EAF1FF 0%, rgba(234,241,255,0) 70%), #FFFFFF',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: 'radial-gradient(#0065F4 1px, transparent 1px)',
                backgroundSize: '28px 28px',
                opacity: 0.07,
                maskImage: 'radial-gradient(700px 500px at 50% 20%, #000 20%, transparent 80%)',
                WebkitMaskImage: 'radial-gradient(700px 500px at 50% 20%, #000 20%, transparent 80%)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 180,
                background: 'linear-gradient(180deg, rgba(247,248,250,0) 0%, #F7F8FA 100%)',
                pointerEvents: 'none',
              }}
            />
            <div
              className="home-blog-hero-grid"
              style={{
                position: 'relative',
                maxWidth: 1200,
                margin: '0 auto',
                display: 'grid',
                gridTemplateColumns: '1.05fr 0.95fr',
                gap: 56,
                alignItems: 'center',
              }}
            >
              <div>
                <h1
                  style={{
                    margin: 0,
                    fontSize: 'clamp(32px,6vw,56px)',
                    lineHeight: 1.06,
                    letterSpacing: '-0.03em',
                    fontWeight: 700,
                    color: '#111827',
                  }}
                >
                  {c.heroTitleBefore}
                  <br />
                  &amp; <span style={{ color: '#0065F4' }}>{c.heroTitleHighlight}</span>
                </h1>
                <p
                  style={{
                    margin: '20px 0 0',
                    maxWidth: 520,
                    fontSize: 'clamp(16px,2.2vw,19px)',
                    lineHeight: 1.6,
                    color: '#374151',
                  }}
                >
                  {c.heroSub}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 32, flexWrap: 'wrap' }}>
                  <a
                    href="#clanky"
                    className="home-btn-primary"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 46,
                      padding: '0 24px',
                      background: '#0065F4',
                      color: '#fff',
                      fontSize: 15,
                      fontWeight: 600,
                      lineHeight: 1,
                      borderRadius: 10,
                    }}
                  >
                    {c.heroCta}
                  </a>
                  <span style={{ fontSize: 14, color: '#6B7280' }}>{c.followUs}</span>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <SocialLinks size="hero" />
                  </div>
                </div>
              </div>

              <div className="home-blog-illu" style={{ position: 'relative', width: 'min(460px,100%)', height: 400, justifySelf: 'end' }}>
                <img
                  src="/home/macbook-smeny.png"
                  alt="Ompli"
                  style={{
                    position: 'absolute',
                    inset: '22px 10px 30px 18px',
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    margin: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                  }}
                />
                <div
                  className="home-float-inner"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: 238,
                    background: '#fff',
                    border: '1px solid #EDF0F5',
                    borderRadius: 18,
                    padding: 14,
                    boxShadow: '0 16px 36px rgba(17,24,39,0.08)',
                    animation: 'home-floatCard 5.4s ease-in-out infinite',
                    rotate: '-3deg',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '16 / 9',
                      borderRadius: 11,
                      background: 'repeating-linear-gradient(135deg,#EAF1FF 0 8px,#F3F7FF 8px 16px)',
                      border: '1px solid #E3EBFB',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HomeIcon name="document" size={28} trigger="loop-on-hover" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 11, fontSize: 10, color: '#6B7280' }}>
                    <HomeIcon name="calendar" size={14} trigger="loop-on-hover" />
                    {formatBlogDate(heroPost?.publishedAt, lang) || '-'}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, lineHeight: 1.35, color: '#111827' }}>
                    {heroPost?.title || c.latestTitle}
                  </div>
                </div>

                <div
                  className="home-float-inner"
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 172,
                    width: 206,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 9,
                    animation: 'home-floatCard 6.2s ease-in-out infinite',
                    animationDelay: '0.9s',
                  }}
                >
                  {c.floatTopics.map((label, i) => (
                    <div
                      key={label}
                      style={{
                        background: i === 1 ? '#0065F4' : '#fff',
                        border: i === 1 ? undefined : '1px solid #EDF0F5',
                        borderRadius: 14,
                        padding: '11px 14px',
                        boxShadow:
                          i === 1
                            ? '0 10px 26px rgba(0,101,244,0.22)'
                            : '0 10px 26px rgba(17,24,39,0.07)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 8,
                          background: i === 1 ? 'rgba(255,255,255,0.2)' : '#EAF1FF',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flex: 'none',
                        }}
                      >
                        <HomeIcon
                          name={TOPIC_ICONS[i]}
                          size={14}
                          color={i === 1 ? '#fff' : '#0065F4'}
                          trigger="loop-on-hover"
                        />
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: i === 1 ? '#fff' : '#111827',
                          lineHeight: 1.3,
                        }}
                      >
                        {label}
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  className="home-float-inner"
                  style={{
                    position: 'absolute',
                    left: 14,
                    bottom: 8,
                    background: '#fff',
                    border: '1px solid #EDF0F5',
                    borderRadius: 16,
                    padding: '13px 17px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    boxShadow: '0 14px 32px rgba(17,24,39,0.08)',
                    animation: 'home-floatCard 4.8s ease-in-out infinite',
                    animationDelay: '1.6s',
                    rotate: '2deg',
                  }}
                >
                  <HomeIcon name="document" size={17} badge badgeSize={34} trigger="loop-on-hover" />
                  <div>
                    <div style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>{c.floatBadgeLabel}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>
                      {c.floatBadgeValue}
                    </div>
                  </div>
                  <span style={{ position: 'relative', width: 10, height: 10, flex: 'none' }}>
                    <span style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#16A34A' }} />
                    <span
                      className="home-pulse-ring"
                      style={{ position: 'absolute', inset: 0, borderRadius: 999, background: '#16A34A' }}
                    />
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section
            id="clanky"
            style={{
              background: '#fff',
              borderTop: '1px solid #EDF0F5',
              padding: 'clamp(56px,9vw,96px) 20px clamp(48px,8vw,88px)',
            }}
          >
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              <h2
                style={{
                  margin: 0,
                  textAlign: 'center',
                  fontSize: 'clamp(28px,5.2vw,44px)',
                  lineHeight: 1.12,
                  letterSpacing: '-0.025em',
                  fontWeight: 700,
                  color: '#111827',
                }}
              >
                {c.latestTitle}
              </h2>
              <p
                style={{
                  margin: '14px auto 0',
                  maxWidth: 560,
                  textAlign: 'center',
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: '#6B7280',
                }}
              >
                {c.latestSub}
              </p>

              {listLoading ? (
                <div
                  className="home-blog-post-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3,1fr)',
                    gap: 24,
                    marginTop: 'clamp(30px,4.5vw,48px)',
                  }}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        background: '#fff',
                        border: '1px solid #EDF0F5',
                        borderRadius: 20,
                        padding: '14px 14px 22px',
                        minHeight: 320,
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          borderRadius: 14,
                          background: '#EAF1FF',
                          opacity: 0.5,
                        }}
                      />
                      <div style={{ height: 14, width: '40%', background: '#E5E7EB', borderRadius: 6, marginTop: 18 }} />
                      <div style={{ height: 20, width: '90%', background: '#E5E7EB', borderRadius: 6, marginTop: 12 }} />
                      <div style={{ height: 14, width: '100%', background: '#F3F4F6', borderRadius: 6, marginTop: 12 }} />
                    </div>
                  ))}
                </div>
              ) : listError ? (
                <p style={{ textAlign: 'center', marginTop: 48, color: '#dc2626' }}>{c.loadError}</p>
              ) : posts.length === 0 ? (
                <div style={{ textAlign: 'center', marginTop: 48 }}>
                  <div style={{ display: 'inline-flex', marginBottom: 12 }}>
                    <HomeIcon name="document" size={40} color="#9CA3AF" trigger="loop" />
                  </div>
                  <p style={{ fontSize: 18, fontWeight: 600, color: '#111827' }}>{c.emptyTitle}</p>
                  <p style={{ marginTop: 8, color: '#6B7280' }}>{c.emptyDesc}</p>
                </div>
              ) : (
                <div
                  className="home-blog-post-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3,1fr)',
                    gap: 24,
                    marginTop: 'clamp(30px,4.5vw,48px)',
                  }}
                >
                  {posts.map((p) => (
                    <article
                      key={p.id}
                      className="home-blog-card"
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        background: '#fff',
                        border: '1px solid #EDF0F5',
                        borderRadius: 20,
                        padding: '14px 14px 22px',
                        transition: 'transform 180ms ease-out, box-shadow 180ms ease-out',
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          aspectRatio: '16 / 9',
                          borderRadius: 14,
                          overflow: 'hidden',
                          background: 'repeating-linear-gradient(135deg,#EAF1FF 0 8px,#F3F7FF 8px 16px)',
                          border: '1px solid #E3EBFB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {p.coverImageUrl ? (
                          <img
                            src={p.coverImageUrl}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <HomeIcon name="document" size={32} color="#7FA9F7" trigger="loop-on-hover" />
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: '18px 8px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#6B7280' }}>
                          <HomeIcon name="calendar" size={16} trigger="loop-on-hover" />
                          {formatBlogDate(p.publishedAt, lang)}
                        </div>
                        <h3
                          style={{
                            margin: '12px 0 0',
                            fontSize: 20,
                            lineHeight: 1.28,
                            letterSpacing: '-0.015em',
                            fontWeight: 700,
                            color: '#111827',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {p.title}
                        </h3>
                        <p
                          style={{
                            margin: '10px 0 0',
                            fontSize: 15,
                            lineHeight: 1.6,
                            color: '#6B7280',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {p.excerpt}
                        </p>
                        <div style={{ marginTop: 'auto', paddingTop: 18, borderTop: '1px solid #EDF0F5' }}>
                          <Link
                            to={`/blog/${p.slug}`}
                            className="home-btn-primary"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              height: 38,
                              padding: '0 16px',
                              background: '#0065F4',
                              color: '#fff',
                              borderRadius: 9,
                              fontSize: 14,
                              fontWeight: 600,
                              lineHeight: 1,
                            }}
                          >
                            {c.readMore}
                            <HomeIcon name="arrow" size={16} color="#fff" trigger="hover" target="a" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {mode === 'article' && (
        <article style={{ background: '#fff', padding: 'clamp(104px,16vw,140px) 20px clamp(56px,9vw,90px)' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ maxWidth: 860, margin: '0 auto' }}>
              <Link
                to="/blog"
                className="home-blog-back"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#6B7280',
                  transition: 'color 150ms ease-out, gap 150ms ease-out',
                }}
              >
                <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
                  <HomeIcon name="arrow" size={18} color="#6B7280" hoverColor="#0065F4" trigger="hover" target="a" />
                </span>
                {c.backToBlog}
              </Link>

              {articleLoading ? (
                <div style={{ marginTop: 40 }} className="space-y-4">
                  <div style={{ height: 40, width: '80%', background: '#E5E7EB', borderRadius: 8 }} />
                  <div style={{ height: 16, width: '30%', background: '#F3F4F6', borderRadius: 6 }} />
                  <div style={{ height: 80, width: '100%', background: '#F3F4F6', borderRadius: 8, marginTop: 24 }} />
                </div>
              ) : articleMissing || !article ? (
                <div style={{ marginTop: 40 }}>
                  <h1 style={{ fontSize: 32, fontWeight: 700, color: '#111827' }}>{c.notFoundTitle}</h1>
                  <p style={{ marginTop: 12, color: '#6B7280' }}>{c.notFoundDesc}</p>
                </div>
              ) : (
                <>
                  <h1
                    style={{
                      margin: '28px 0 0',
                      fontSize: 'clamp(30px,5.4vw,46px)',
                      lineHeight: 1.12,
                      letterSpacing: '-0.03em',
                      fontWeight: 700,
                      color: '#111827',
                    }}
                  >
                    {article.title}
                  </h1>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, fontSize: 14, color: '#6B7280' }}>
                    <HomeIcon name="calendar" size={16} trigger="loop-on-hover" />
                    {formatBlogDate(article.publishedAt, lang)}
                    <span style={{ color: '#D1D5DB' }}>·</span>
                    <HomeIcon name="clock" size={16} trigger="loop-on-hover" />
                    {c.readMinutes(estimateReadMinutes(article.bodyMarkdown))}
                  </div>
                  {article.lead ? (
                    <p style={{ margin: '30px 0 0', fontSize: 19, lineHeight: 1.7, color: '#374151' }}>{article.lead}</p>
                  ) : null}

                  {(article.coverImageUrl || article.coverCaption) && (
                    <figure style={{ margin: 'clamp(28px,4vw,40px) 0 0', maxWidth: 560 }}>
                      <div
                        style={{
                          position: 'relative',
                          width: '100%',
                          aspectRatio: '16 / 9',
                          borderRadius: 20,
                          overflow: 'hidden',
                          background: 'repeating-linear-gradient(135deg,#EAF1FF 0 10px,#F3F7FF 10px 20px)',
                          border: '1px solid #E3EBFB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {article.coverImageUrl ? (
                          <img
                            src={article.coverImageUrl}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <HomeIcon name="document" size={40} color="#7FA9F7" trigger="loop-on-hover" />
                        )}
                      </div>
                      {article.coverCaption ? (
                        <figcaption style={{ marginTop: 12, fontSize: 14, color: '#6B7280' }}>
                          {article.coverCaption}
                        </figcaption>
                      ) : null}
                    </figure>
                  )}

                  <div className="home-blog-prose" style={{ marginTop: 'clamp(28px,4vw,42px)' }}>
                    <DocsMarkdown content={article.bodyMarkdown} currentPath="" />
                  </div>

                  <div
                    style={{
                      marginTop: 'clamp(40px,6vw,64px)',
                      padding: 'clamp(26px,4vw,36px)',
                      borderRadius: 20,
                      background: '#0065F4',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 20,
                    }}
                  >
                    <div style={{ maxWidth: 420 }}>
                      <div
                        style={{
                          fontSize: 'clamp(20px,3vw,26px)',
                          fontWeight: 700,
                          lineHeight: 1.25,
                          letterSpacing: '-0.02em',
                          color: '#fff',
                        }}
                      >
                        {c.ctaTitle}
                      </div>
                      <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.6, color: 'rgba(255,255,255,0.82)' }}>
                        {c.ctaSub}
                      </p>
                    </div>
                    <a
                      href="/register"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        height: 48,
                        padding: '0 24px',
                        borderRadius: 10,
                        background: '#fff',
                        color: '#0065F4',
                        fontSize: 15,
                        fontWeight: 600,
                      }}
                      className="home-blog-cta-white"
                    >
                      {c.ctaButton}
                      <HomeIcon name="arrow" size={16} color="#0065F4" trigger="hover" target="a" />
                    </a>
                  </div>

                  {related.length > 0 && (
                    <div style={{ marginTop: 'clamp(40px,6vw,64px)', paddingTop: 28, borderTop: '1px solid #EDF0F5' }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: '#6B7280',
                        }}
                      >
                        {c.relatedTitle}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 14 }}>
                        {related.map((r) => (
                          <Link
                            key={r.id}
                            to={`/blog/${r.slug}`}
                            className="home-blog-related"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 16,
                              width: '100%',
                              textAlign: 'left',
                              borderRadius: 12,
                              padding: '16px 14px',
                              fontSize: 17,
                              fontWeight: 600,
                              color: '#111827',
                              lineHeight: 1.35,
                              transition: 'background 150ms ease-out',
                            }}
                          >
                            <span>{r.title}</span>
                            <HomeIcon name="arrow" size={18} color="#0065F4" trigger="hover" target="a" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </article>
      )}

      {footer}
    </div>
  );
};

export default BlogLanding;
